'use client';

import { clsx } from 'clsx';
import { FC, PropsWithChildren, useState } from 'react';

import { IconQuestionMarkEmpty } from 'src/general/Icons/IconQuestionMarkEmpty';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

import { BasicModal } from '../BasicModal/BasicModal';
import { Button } from '../Button/Button';
import { Shade } from '../Shade/Shade';

type Props = {
  entryId: string;
  title: string;
  modalTitle: string;
  className?: string;
  closeButton?: boolean;
  event?: string;
  variant?: 'full' | 'icon-only';
};

export const HelperModal: FC<PropsWithChildren<Props>> = ({
  entryId,
  title,
  modalTitle,
  children,
  className,
  closeButton,
  event,
  variant = 'full',
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const onOpen = () => {
    setIsOpen(true);
    pushToDataLayer({
      event: event ?? `open_helper_modal`,
      value: entryId,
      label: title,
    });
  };

  const onClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      <button
        className={clsx(
          'c-shade_container c-shade_container--grey c-fs-body2 relative block cursor-pointer overflow-hidden font-semibold',
          {
            ['size-7 rounded-full p-1']: variant === 'icon-only',
            ['w-full rounded-xs bg-for-neu-tie3 px-2 py-1.5']: variant === 'full',
          },
        )}
        onClick={onOpen}
        type="button"
        data-testid="HelperModal-trigger"
      >
        <Shade noBackground />
        {variant === 'icon-only' ? (
          <IconQuestionMarkEmpty size={20} />
        ) : (
          <span className="pointer-events-none relative flex w-full items-start justify-start gap-x-1.5">
            <IconQuestionMarkEmpty size={20} />
            <span className="pt-px">{title}</span>
          </span>
        )}
      </button>
      <BasicModal
        id={entryId}
        isOpen={isOpen}
        onClose={onClose}
        title={modalTitle}
        className={className}
        footer={closeButton && <Button onClick={onClose}>Close</Button>}
      >
        {children}
      </BasicModal>
    </>
  );
};
